(function () {
  var API_BASE_FALLBACK = "https://api.shimmergift.com";
  var POLL_INTERVAL_MS = 1500;
  var POLL_TIMEOUT_MS = 5 * 60 * 1000;
  var TEXTURE_LOAD_TIMEOUT_MS = 30000;
  var PREVIEW_TEXTURE_LONG_EDGE = 1400;
  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  var ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/tiff"];
  var API_REQUEST_TIMEOUT_MS = 60000;
  var STATUS_REQUEST_TIMEOUT_MS = 30000;
  var CART_REQUEST_TIMEOUT_MS = 30000;

  function bytesFromBase64(base64) {
    var binary = window.atob(base64);
    var bytes = new Uint8Array(binary.length);
    for (var index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }

  function base64ToObjectUrl(base64) {
    var bytes = bytesFromBase64(base64);
    var blob = new Blob([bytes], { type: "image/png" });
    return URL.createObjectURL(blob);
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function fetchWithTimeout(url, options, timeoutMs, timeoutMessage) {
    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () {
      controller.abort();
    }, timeoutMs);
    var requestOptions = Object.assign({}, options || {}, {
      signal: controller.signal
    });

    return fetch(url, requestOptions).catch(function (error) {
      if (error && error.name === "AbortError") {
        throw new Error(timeoutMessage || "The request took too long. Please try again.");
      }
      throw error;
    }).finally(function () {
      window.clearTimeout(timeoutId);
    });
  }

  async function responseErrorMessage(response, fallbackMessage) {
    if (!response || response.status >= 500) {
      return fallbackMessage;
    }

    try {
      var payload = await response.json();
      var message = payload && (
        typeof payload.detail === "string" && payload.detail ||
        typeof payload.description === "string" && payload.description ||
        typeof payload.message === "string" && payload.message
      );
      return message && message.length <= 240 ? message : fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  }

  function buildStatusMessage(job) {
    if (job.status === "queued") {
      if (job.position === 1) {
        return "You're next in the preview queue.";
      }
      if (job.position) {
        if (job.queue_length) {
          return "Queue position " + job.position + " of " + job.queue_length + ".";
        }
        return "Queue position " + job.position + ".";
      }
      return "Queued for preview processing.";
    }
    if (job.status === "processing") {
      return "Rendering your holographic preview.";
    }
    if (job.status === "succeeded") {
      return "Preview ready.";
    }
    return "Preview processing failed.";
  }

  function buildQueuePhase(job) {
    if (job.status === "queued") {
      if (job.position === 1) {
        return "You're first in line for the next available render worker.";
      }
      return "Your image is waiting in line while another preview finishes.";
    }
    if (job.status === "processing") {
      return "We are building the holographic image.";
    }
    if (job.status === "succeeded") {
      return "Your preview is ready.";
    }
    return "We couldn't finish this preview.";
  }

  function HologramViewer(root) {
    this.root = root;
    this.card = root.querySelector("[data-shimmergift-card]");
    this.canvas = root.querySelector("[data-shimmergift-canvas]");
    this.empty = root.querySelector("[data-shimmergift-empty]");
    this.gl = null;
    this.program = null;
    this.frameId = null;
    this.startTime = 0;
    this.orientation = "portrait";
    this.foregroundUrl = "";
    this.backgroundUrl = "";
    this.texturesReady = false;
    this.texBg = null;
    this.texFg = null;
    this.tiltAmplitude = 6;
    this.periodSeconds = 7;
    this.uBg = null;
    this.uFg = null;
    this.uRot = null;
    this.uLight = null;
    this.uAspect = null;
    this.uDrift = null;
    this.tiltX = 0;
    this.tiltY = 0;
    this.pointerTiltX = null;
    this.pointerTiltY = null;
    this.interactionsBound = false;
    this.paused = true;
    this.reducedMotion = typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  HologramViewer.prototype.vertexShaderSource = [
    "attribute vec2 a_pos;",
    "varying vec2 v_uv;",
    "void main() {",
    "  v_uv = vec2(a_pos.x * 0.5 + 0.5, a_pos.y * 0.5 + 0.5);",
    "  gl_Position = vec4(a_pos, 0.0, 1.0);",
    "}"
  ].join("\n");

  HologramViewer.prototype.fragmentShaderSource = [
    "precision mediump float;",
    "uniform sampler2D u_bg;",
    "uniform sampler2D u_fg;",
    "uniform mat3 u_rot;",
    "uniform vec3 u_light;",
    "uniform float u_aspect;",
    "uniform float u_drift;",
    "varying vec2 v_uv;",
    "float luma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }",
    "vec3 hue2rgb(float h) { return clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0); }",
    "float hash21(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }",
    "vec2 hash22(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.xx + p3.yz) * p3.zy); }",
    "float vnoise(vec2 p) { vec2 i = floor(p); vec2 f = p - i; f = f * f * (3.0 - 2.0 * f); float a = hash21(i); float b = hash21(i + vec2(1.0, 0.0)); float c = hash21(i + vec2(0.0, 1.0)); float d = hash21(i + vec2(1.0, 1.0)); return mix(mix(a, b, f.x), mix(c, d, f.x), f.y); }",
    "void gradEndpoints(float i, out vec3 a, out vec3 b) { vec3 green = vec3(0.10, 0.80, 0.22); vec3 yellow = vec3(1.00, 0.90, 0.12); vec3 turq = vec3(0.10, 0.85, 0.72); vec3 blue = vec3(0.15, 0.35, 0.95); vec3 cyan = vec3(0.20, 0.90, 1.00); vec3 orange = vec3(1.00, 0.55, 0.10); vec3 pink = vec3(1.00, 0.35, 0.70); vec3 red = vec3(0.95, 0.16, 0.16); vec3 purple = vec3(0.60, 0.20, 0.85); vec3 white = vec3(1.0); if (i < 0.5) { a = green; b = yellow; } else if (i < 1.5) { a = green; b = turq; } else if (i < 2.5) { a = blue; b = cyan; } else if (i < 3.5) { a = orange; b = yellow; } else if (i < 4.5) { a = pink; b = red; } else if (i < 5.5) { a = pink; b = yellow; } else if (i < 6.5) { a = red; b = mix(red, white, 0.55); } else if (i < 7.5) { a = green; b = mix(green, white, 0.55); } else if (i < 8.5) { a = yellow; b = mix(yellow, white, 0.55); } else if (i < 9.5) { a = purple; b = mix(purple, white, 0.55); } else { a = turq; b = mix(turq, white, 0.55); } }",
    "vec4 foilLayer(float scale) { vec2 world = v_uv * scale; vec2 warp1 = vec2(vnoise(v_uv * 4.3 + 1.7), vnoise(v_uv * 4.3 + 9.1)) - 0.5; vec2 warp2 = vec2(vnoise(v_uv * 9.7 + 4.2), vnoise(v_uv * 9.7 + 12.6)) - 0.5; world += warp1 * 1.5 + warp2 * 0.7; vec2 sk = vec2(world.x - world.y * 0.57735, world.y * 1.15470); vec2 ri = floor(sk); vec2 rf = sk - ri; vec2 va; vec2 vb; if ((rf.x + rf.y) >= 1.0) { va = ri + vec2(1.0, 0.0); vb = ri + vec2(1.0, 1.0); } else { va = ri; vb = ri + vec2(1.0, 0.0); } vec2 vc = ri + vec2(0.0, 1.0); vec2 cell1 = (va + vb + vc) / 3.0; float JIT = 0.42; vec2 pa = va + (hash22(va) * 2.0 - 1.0) * JIT; vec2 pb = vb + (hash22(vb) * 2.0 - 1.0) * JIT; vec2 pc = vc + (hash22(vc) * 2.0 - 1.0) * JIT; float denom = (pb.y - pc.y) * (pa.x - pc.x) + (pc.x - pb.x) * (pa.y - pc.y); denom = sign(denom) * max(abs(denom), 0.001); float l1 = ((pb.y - pc.y) * (sk.x - pc.x) + (pc.x - pb.x) * (sk.y - pc.y)) / denom; float l2 = ((pc.y - pa.y) * (sk.x - pc.x) + (pa.x - pc.x) * (sk.y - pc.y)) / denom; float l3 = 1.0 - l1 - l2; float bary_min = min(l1, min(l2, l3)); float border = smoothstep(0.0, 0.10, bary_min); vec2 gv = rf; vec2 near_pt = cell1 - ri; float face_tone = hash21(cell1 + vec2(31.7, 12.4)); float facet_shade = (face_tone - 0.5) * 0.018; float seam = (1.0 - border) * 0.012; float facet = facet_shade - seam; vec3 Nrm = u_rot[2]; vec3 Pw = u_rot * vec3((v_uv - 0.5) * vec2(u_aspect, 1.0), 0.0); vec3 Vv = vec3(0.0, 0.0, 1.0); vec3 Lv = normalize(u_light - Pw); vec3 Hv = normalize(Lv + Vv); vec3 Lv2 = normalize(vec3(-u_light.x, -u_light.y, u_light.z) - Pw); vec3 Hv2 = normalize(Lv2 + Vv); float macro = max(max(dot(Nrm, Hv), dot(Nrm, Hv2)), 0.0); float hotspot = pow(macro, 6.0); facet += hotspot * (face_tone * 0.03 + 0.008); vec2 pertB = (hash22(cell1 + vec2(5.17, 9.41)) * 2.0 - 1.0); vec2 pertS = (hash22(cell1 + vec2(2.93, 7.71)) * 2.0 - 1.0); vec3 MlocalB = normalize(vec3(pertB * 1.4, 1.0)); vec3 MlocalS = normalize(vec3(pertS * 4.5, 1.0)); vec3 MvB = u_rot * MlocalB; vec3 MvS = u_rot * MlocalS; float microB = max(max(dot(MvB, Hv), dot(MvB, Hv2)), 0.0); float microS = max(max(dot(MvS, Hv), dot(MvS, Hv2)), 0.0); float broad = 0.22 + 0.78 * pow(microB, 1.5); float sharp = pow(microS, 9.0); vec2 df = v_uv * 1.7; vec2 dfi = floor(df); vec2 dfs = df - dfi; dfs = dfs * dfs * (3.0 - 2.0 * dfs); float density = mix(mix(hash21(dfi + vec2(7.1, 2.3)), hash21(dfi + vec2(8.1, 2.3)), dfs.x), mix(hash21(dfi + vec2(7.1, 3.3)), hash21(dfi + vec2(8.1, 3.3)), dfs.x), dfs.y); density = mix(0.92, 1.0, density); float face_pick = hash21(cell1 + vec2(19.7, 6.1)); float dens_gate = smoothstep(density + 0.12, density - 0.12, face_pick); float peak = hash21(cell1 + vec2(3.9, 14.2)); float intensity = mix(0.35, 1.0, peak * peak); float vivid_pick = 1.0; float shard_op = pow(hash21(cell1 + vec2(8.3, 27.1)), 2.4); shard_op = mix(0.12, 1.0, shard_op); float faint = broad * 0.185 * dens_gate * intensity * shard_op; float vivid_act = sharp * vivid_pick * dens_gate * intensity; float activation = max(faint, vivid_act); float hmag = length(Hv - Nrm * dot(Hv, Nrm)); float gfreq = mix(0.8, 1.8, hash21(cell1 + vec2(11.3, 4.7))); float gphase = hash21(cell1 + vec2(2.1, 8.6)); float gangle = hash21(cell1 + vec2(7.7, 3.3)) * 6.2832; float grad_t = dot(gv - near_pt, vec2(cos(gangle), sin(gangle))); float graw = gphase + hmag * gfreq + grad_t * 0.5; float gt = abs(fract(graw) * 2.0 - 1.0); float gidx = floor(hash21(cell1 + vec2(15.2, 41.9)) * 11.0); vec3 gA; vec3 gB; gradEndpoints(gidx, gA, gB); vec3 col = mix(gA, gB, gt); float colorAmt = smoothstep(0.04, 0.20, vivid_act); vec3 silver = vec3(0.85, 0.87, 0.92); vec3 faintCol = mix(silver, col, 0.40); vec3 faceCol = mix(faintCol, col, colorAmt); float mud = 0.78 + 0.44 * vnoise(v_uv * 130.0); faceCol *= mud; vec3 vivid = border * faceCol * activation * 6.0; return vec4(vivid, facet); }",
    "void main() { vec4 foil = foilLayer(30.0); vec3 foil_active = foil.rgb; float facet = foil.a; vec4 bg = texture2D(u_bg, v_uv); float holo = clamp((1.0 - bg.a) / 0.30, 0.0, 1.0); holo = pow(holo, 3.0); vec3 substrate = vec3(0.86, 0.87, 0.90); vec3 bg_rgb = bg.rgb; bg_rgb = mix(substrate, bg_rgb, 0.62); float bg_luma = luma(bg_rgb); bg_rgb = mix(vec3(bg_luma), bg_rgb, 0.55); bg_rgb = mix(vec3(0.58), bg_rgb, 0.82); bg_rgb += facet * holo; float diag = dot(v_uv, normalize(vec2(1.0, 0.85))); float rb_raw = diag * 0.65 + u_drift; float rb_tri = abs(fract(rb_raw) * 2.0 - 1.0); vec3 rainbow = hue2rgb(rb_tri * 0.64); bg_rgb = mix(bg_rgb, bg_rgb * (0.85 + rainbow * 0.45), 0.14 * holo); float strength = max(foil_active.r, max(foil_active.g, foil_active.b)); strength = clamp(strength, 0.0, 1.0); vec3 pure_hue = foil_active / max(strength, 0.001); float mixAmt = min(strength * holo, 0.90); vec3 bg_lit = mix(bg_rgb, pure_hue, mixAmt); vec4 fg = texture2D(u_fg, v_uv); gl_FragColor = vec4(mix(bg_lit, fg.rgb, fg.a), 1.0); }"
  ].join("\n");

  HologramViewer.prototype.init = function () {
    if (this.gl && this.program) {
      return true;
    }

    this.gl = this.gl || this.canvas.getContext("webgl");
    if (!this.gl) {
      return false;
    }

    var gl = this.gl;
    var program = gl.createProgram();
    gl.attachShader(program, this.compileShader(gl.VERTEX_SHADER, this.vertexShaderSource));
    gl.attachShader(program, this.compileShader(gl.FRAGMENT_SHADER, this.fragmentShaderSource));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error("Program link error: " + gl.getProgramInfoLog(program));
    }

    this.program = program;

    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      -1, 1,
      1, -1,
      1, 1
    ]), gl.STATIC_DRAW);

    var posLoc = gl.getAttribLocation(program, "a_pos");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
    gl.useProgram(program);

    this.uBg = gl.getUniformLocation(program, "u_bg");
    this.uFg = gl.getUniformLocation(program, "u_fg");
    this.uRot = gl.getUniformLocation(program, "u_rot");
    this.uLight = gl.getUniformLocation(program, "u_light");
    this.uAspect = gl.getUniformLocation(program, "u_aspect");
    this.uDrift = gl.getUniformLocation(program, "u_drift");
    this.bindInteractions();
    this.startTime = performance.now();
    this.render = this.render.bind(this);
    if (!this.paused) {
      this.frameId = window.requestAnimationFrame(this.render);
    }
    return true;
  };

  HologramViewer.prototype.compileShader = function (type, source) {
    var shader = this.gl.createShader(type);
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      throw new Error("Shader compile error: " + this.gl.getShaderInfoLog(shader));
    }
    return shader;
  };

  HologramViewer.prototype.bindInteractions = function () {
    if (this.interactionsBound) {
      return;
    }

    var self = this;
    this.interactionsBound = true;

    this.card.addEventListener("pointermove", function (event) {
      var bounds = self.card.getBoundingClientRect();
      if (!bounds.width || !bounds.height) {
        return;
      }
      var x = (event.clientX - bounds.left) / bounds.width;
      var y = (event.clientY - bounds.top) / bounds.height;
      self.pointerTiltY = (x - 0.5) * self.tiltAmplitude * 2;
      self.pointerTiltX = (0.5 - y) * self.tiltAmplitude;
    });

    this.card.addEventListener("pointerleave", function () {
      self.pointerTiltX = null;
      self.pointerTiltY = null;
    });
  };

  HologramViewer.prototype.loadTexture = function (url, unit) {
    var gl = this.gl;
    return new Promise(function (resolve, reject) {
      var texture = gl.createTexture();
      var image = new Image();
      var settled = false;
      var timeoutId = window.setTimeout(function () {
        if (settled) {
          return;
        }
        settled = true;
        image.src = "";
        gl.deleteTexture(texture);
        reject(new Error("The holographic preview took too long to load."));
      }, TEXTURE_LOAD_TIMEOUT_MS);

      function fail(error) {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        gl.deleteTexture(texture);
        reject(error);
      }

      image.onload = function () {
        if (settled) {
          return;
        }

        try {
          var maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) || PREVIEW_TEXTURE_LONG_EDGE;
          var longEdge = Math.min(PREVIEW_TEXTURE_LONG_EDGE, maxTextureSize);
          var source = image;

          if (Math.max(image.naturalWidth, image.naturalHeight) > longEdge) {
            var scale = longEdge / Math.max(image.naturalWidth, image.naturalHeight);
            var previewCanvas = document.createElement("canvas");
            previewCanvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
            previewCanvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
            var context = previewCanvas.getContext("2d");
            if (!context) {
              throw new Error("The browser could not prepare the preview texture.");
            }
            context.drawImage(image, 0, 0, previewCanvas.width, previewCanvas.height);
            source = previewCanvas;
          }

          gl.activeTexture(gl.TEXTURE0 + unit);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

          if (gl.isContextLost() || gl.getError() !== gl.NO_ERROR) {
            throw new Error("The browser could not render the holographic preview.");
          }

          settled = true;
          window.clearTimeout(timeoutId);
          resolve(texture);
        } catch (error) {
          fail(error instanceof Error ? error : new Error("The holographic texture could not be loaded."));
        }
      };
      image.onerror = function () {
        fail(new Error("The generated holographic layer could not be loaded."));
      };
      image.decoding = "async";
      image.src = url;
    });
  };

  HologramViewer.prototype.setImages = function (foregroundUrl, backgroundUrl, orientation) {
    this.foregroundUrl = foregroundUrl;
    this.backgroundUrl = backgroundUrl;
    this.orientation = orientation || "portrait";
    this.card.style.setProperty(
      "--shimmergift-aspect-ratio",
      this.orientation === "portrait" ? "8.5 / 11" : "11 / 8.5"
    );

    if (!this.init()) {
      this.empty.hidden = false;
      return Promise.reject(new Error("This browser does not support the holographic preview."));
    }

    var self = this;
    return this.reloadTextures().then(function () {
      self.empty.hidden = true;
    });
  };

  HologramViewer.prototype.reloadTextures = function () {
    var self = this;
    if (!this.gl || !this.foregroundUrl || !this.backgroundUrl) {
      return Promise.resolve();
    }

    self.texturesReady = false;

    var previousBackground = self.texBg;
    var previousForeground = self.texFg;
    var nextBackground = null;

    return this.loadTexture(this.backgroundUrl, 0).then(function (backgroundTexture) {
      nextBackground = backgroundTexture;
      return self.loadTexture(self.foregroundUrl, 1);
    }).then(function (foregroundTexture) {
      self.texBg = nextBackground;
      self.texFg = foregroundTexture;
      self.texturesReady = true;
      if (self.reducedMotion && !self.paused && self.frameId === null) {
        self.frameId = window.requestAnimationFrame(self.render);
      }
      if (previousBackground) {
        self.gl.deleteTexture(previousBackground);
      }
      if (previousForeground) {
        self.gl.deleteTexture(previousForeground);
      }
    }).catch(function (error) {
      if (nextBackground) {
        self.gl.deleteTexture(nextBackground);
      }
      throw error;
    });
  };

  HologramViewer.prototype.reset = function () {
    this.texturesReady = false;
    this.foregroundUrl = "";
    this.backgroundUrl = "";
    this.pointerTiltX = null;
    this.pointerTiltY = null;
    this.card.style.removeProperty("transform");
    this.card.style.setProperty("--shimmergift-aspect-ratio", "8.5 / 11");
    this.empty.hidden = false;

    if (!this.gl) {
      return;
    }
    if (this.texBg) {
      this.gl.deleteTexture(this.texBg);
      this.texBg = null;
    }
    if (this.texFg) {
      this.gl.deleteTexture(this.texFg);
      this.texFg = null;
    }
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);
  };

  HologramViewer.prototype.pause = function () {
    this.paused = true;
    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  };

  HologramViewer.prototype.resume = function () {
    if (!this.paused && this.frameId !== null) {
      return;
    }
    this.paused = false;
    if (this.gl && this.program && this.frameId === null) {
      this.startTime = performance.now();
      this.frameId = window.requestAnimationFrame(this.render);
    }
  };

  HologramViewer.prototype.render = function (now) {
    this.frameId = null;
    if (!this.gl || this.paused) {
      return;
    }
    if (!this.root.isConnected) {
      this.frameId = null;
      return;
    }
    if (!this.canvas.clientWidth || !this.canvas.clientHeight || document.hidden) {
      if (!this.paused) {
        this.frameId = window.requestAnimationFrame(this.render);
      }
      return;
    }

    var elapsed = this.reducedMotion ? 0 : now - this.startTime;
    var periodMs = Math.max(1000, this.periodSeconds * 1000);
    var tiltPhase = (elapsed % periodMs) / periodMs;
    var phase = tiltPhase * Math.PI * 2;
    var p = Math.sin(phase);
    var q = Math.cos(phase);
    var t = elapsed / 1000;
    var wobX = Math.sin(t * 0.83 + 1.7) * 0.35 + Math.sin(t * 1.47) * 0.2;
    var wobY = Math.sin(t * 0.61) * 0.35 + Math.sin(t * 1.19 + 2.3) * 0.2;

    this.tiltY = this.reducedMotion
      ? 0
      : this.pointerTiltY == null
        ? (p + wobY) * this.tiltAmplitude
        : this.pointerTiltY;
    this.tiltX = this.reducedMotion
      ? 0
      : this.pointerTiltX == null
        ? (q + wobX) * this.tiltAmplitude * 0.5
        : this.pointerTiltX;
    this.card.style.transform = this.reducedMotion
      ? "none"
      : "rotateX(" + this.tiltX + "deg) rotateY(" + this.tiltY + "deg)";

    var CAP = 0.80;
    var softClamp = function (value) {
      return CAP * Math.tanh(value / CAP);
    };
    var ax = softClamp((this.tiltX * Math.PI) / 180 * 12);
    var ay = softClamp((this.tiltY * Math.PI) / 180 * 12);
    var ca = Math.cos(ax);
    var sa = Math.sin(ax);
    var cb = Math.cos(ay);
    var sb = Math.sin(ay);
    var rot = new Float32Array([
      cb, 0, -sb,
      sb * sa, ca, cb * sa,
      sb * ca, -sa, cb * ca
    ]);
    var light = [0.18, 0.42, 1.15];
    var aspect = this.orientation === "portrait" ? 8.5 / 11 : 11 / 8.5;
    var drift = this.reducedMotion ? 0 : Math.sin(phase) * 0.09;
    var dpr = window.devicePixelRatio || 1;
    var width = Math.round(this.canvas.clientWidth * dpr);
    var height = Math.round(this.canvas.clientHeight * dpr);

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.gl.viewport(0, 0, width, height);
    }

    if (this.texturesReady) {
      this.gl.activeTexture(this.gl.TEXTURE0);
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texBg);
      this.gl.activeTexture(this.gl.TEXTURE1);
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.texFg);
      this.gl.uniform1i(this.uBg, 0);
      this.gl.uniform1i(this.uFg, 1);
      this.gl.uniformMatrix3fv(this.uRot, false, rot);
      this.gl.uniform3f(this.uLight, light[0], light[1], light[2]);
      this.gl.uniform1f(this.uAspect, aspect);
      this.gl.uniform1f(this.uDrift, drift);
      this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }

    if (!this.paused && !this.reducedMotion) {
      this.frameId = window.requestAnimationFrame(this.render);
    }
  };

  function initShimmergiftProduct(root) {
    if (root.dataset.shimmergiftInitialized === "true") {
      return;
    }

    var apiBase = root.getAttribute("data-shimmergift-api-base") || API_BASE_FALLBACK;
    var form = root.querySelector(".shimmergift-product-form__form");
    var addButton = root.querySelector("[data-shimmergift-add-to-cart]");
    var addText = root.querySelector("[data-shimmergift-add-text]");
    var uploadTriggers = root.querySelectorAll("[data-shimmergift-upload-trigger]");
    var openPreviewButton = root.querySelector("[data-shimmergift-open-preview]");
    var fileInput = root.querySelector("[data-shimmergift-file-input]");
    var dropzone = root.querySelector("[data-shimmergift-dropzone]");
    var statusNode = root.querySelector("[data-shimmergift-status]");
    var uploadStatusNode = root.querySelector("[data-shimmergift-upload-status]");
    var errorNode = root.querySelector("[data-shimmergift-error]");
    var fileNameNode = root.querySelector("[data-shimmergift-file-name]");
    var propertiesNode = root.querySelector("[data-shimmergift-properties]");
    var dynamicCheckout = root.querySelector("[data-shimmergift-dynamic-checkout]");
    var expressNote = root.querySelector("[data-shimmergift-express-note]");
    var modal = root.querySelector("[data-shimmergift-modal]");
    var modalDialog = root.querySelector("[data-shimmergift-modal-dialog]");
    var modalTitle = root.querySelector("[data-shimmergift-modal-title]");
    var modalMessage = root.querySelector("[data-shimmergift-modal-message]");
    var modalPhase = root.querySelector("[data-shimmergift-modal-phase]");
    var modalProgress = root.querySelector("[data-shimmergift-modal-progress]");
    var queueStats = root.querySelector("[data-shimmergift-queue-stats]");
    var modalReady = root.querySelector("[data-shimmergift-modal-ready]");
    var modalVariantSelect = root.querySelector("[data-shimmergift-variant-select]");
    var retryUploadButton = root.querySelector("[data-shimmergift-retry-upload]");
    var previewBadge = root.querySelector("[data-shimmergift-preview-badge]");
    var queuePosition = root.querySelector("[data-shimmergift-queue-position]");
    var queueLength = root.querySelector("[data-shimmergift-queue-length]");
    var busyOverlay = root.querySelector("[data-shimmergift-busy]");
    var closeButtons = root.querySelectorAll("[data-shimmergift-modal-close]");
    var variantsElement = root.querySelector("product-variants");
    var variantsScript = root.querySelector("product-variants script[type='application/json']");
    var viewerRoot = root.querySelector("[data-shimmergift-viewer-shell]");

    if (
      !form || !addButton || !addText || !uploadTriggers.length || !openPreviewButton ||
      !fileInput || !dropzone || !propertiesNode || !modal || !modalDialog ||
      !modalTitle || !modalMessage || !modalPhase || !modalProgress || !queueStats || !modalReady ||
      !retryUploadButton ||
      !previewBadge || !busyOverlay || !viewerRoot
    ) {
      return;
    }

    root.dataset.shimmergiftInitialized = "true";

    var viewer = new HologramViewer(viewerRoot);
    var variants = [];
    var lastFocusedElement = null;
    var isolatedElements = [];
    var buyNowLockPending = false;
    var purchaseUnlockTimer = 0;
    var state = {
      file: null,
      previewReady: false,
      previewProcessing: false,
      uploadReady: false,
      uploadProcessing: false,
      uploadMetadata: null,
      addInProgress: false,
      purchaseAction: "",
      previewFailed: false,
      setupFailed: false,
      previewJobStatus: "",
      foregroundUrl: "",
      backgroundUrl: "",
      orientation: "portrait"
    };

    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        variants = [];
      }
    }

    function setText(node, text, hideWhenBlank) {
      if (!node) {
        return;
      }
      node.textContent = text || "";
      if (hideWhenBlank) {
        node.hidden = !text;
      }
    }

    function setError(message) {
      setText(errorNode, message, true);
      if (message) {
        setText(statusNode, "", true);
      }
    }

    function setStatus(message) {
      setText(statusNode, message, true);
    }

    function setUploadStatus(message) {
      setText(uploadStatusNode, message, true);
    }

    function setFileName(name) {
      setText(fileNameNode, name ? "Selected photo: " + name : "", true);
    }

    function setPreviewBadge(text, status) {
      previewBadge.textContent = text || "";
      previewBadge.classList.toggle("is-ready", status === "ready");
      previewBadge.classList.toggle("is-error", status === "error");
    }

    function setModalMode(mode) {
      var showReady = mode === "ready" || mode === "purchase-error";
      var showProgress = mode === "processing" || mode === "saving";
      modalProgress.hidden = !showProgress;
      queueStats.hidden = mode !== "processing" || state.previewJobStatus !== "queued";
      modalReady.hidden = !showReady;
      retryUploadButton.hidden = mode !== "setup-error";
      modalDialog.setAttribute("aria-busy", showProgress ? "true" : "false");

      if (mode === "ready") {
        setPreviewBadge("Preview ready", "ready");
      } else if (mode === "setup-error") {
        setPreviewBadge("Setup needed", "error");
      } else if (mode === "purchase-error" || mode === "error") {
        setPreviewBadge(mode === "purchase-error" ? "Try again" : "Preview failed", "error");
      } else {
        setPreviewBadge(state.previewReady ? "Finishing setup" : "Generating", "processing");
      }
    }

    function isolatePageForModal() {
      var current = modal;
      isolatedElements = [];

      while (current && current !== document.body) {
        var parent = current.parentElement;
        if (!parent) {
          break;
        }

        Array.prototype.forEach.call(parent.children, function (sibling) {
          if (
            sibling === current ||
            /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName) ||
            sibling.matches("input[type='hidden']")
          ) {
            return;
          }

          isolatedElements.push({
            element: sibling,
            hadInert: sibling.hasAttribute("inert"),
            inertValue: sibling.getAttribute("inert"),
            hadAriaHidden: sibling.hasAttribute("aria-hidden"),
            ariaHiddenValue: sibling.getAttribute("aria-hidden")
          });
          sibling.setAttribute("inert", "");
          sibling.setAttribute("aria-hidden", "true");
        });

        current = parent;
      }
    }

    function restorePageAfterModal() {
      isolatedElements.forEach(function (entry) {
        if (entry.hadInert) {
          entry.element.setAttribute("inert", entry.inertValue == null ? "" : entry.inertValue);
        } else {
          entry.element.removeAttribute("inert");
        }

        if (entry.hadAriaHidden) {
          entry.element.setAttribute("aria-hidden", entry.ariaHiddenValue == null ? "" : entry.ariaHiddenValue);
        } else {
          entry.element.removeAttribute("aria-hidden");
        }
      });
      isolatedElements = [];
    }

    function openModal(title, message, phase) {
      if (modal.hidden) {
        lastFocusedElement = document.activeElement;
        modal.hidden = false;
        modalDialog.focus({ preventScroll: true });
        isolatePageForModal();
      }
      document.documentElement.classList.add("shimmergift-modal-open");
      viewer.resume();
      if (title !== undefined) {
        setText(modalTitle, title || "Preparing your holographic preview", false);
      }
      if (message !== undefined) {
        setText(modalMessage, message || "", false);
      }
      if (phase !== undefined) {
        setText(modalPhase, phase || "", false);
      }
      window.requestAnimationFrame(function () {
        if (!modal.hidden) {
          modalDialog.focus();
        }
      });
    }

    function closeModal(restoreFocus) {
      if (modal.hidden) {
        return;
      }
      viewer.pause();
      modal.hidden = true;
      document.documentElement.classList.remove("shimmergift-modal-open");
      restorePageAfterModal();
      if (
        restoreFocus !== false && lastFocusedElement && lastFocusedElement.isConnected &&
        typeof lastFocusedElement.focus === "function"
      ) {
        lastFocusedElement.focus();
      }
    }

    function updateQueueStats(job) {
      state.previewJobStatus = job.status || "";
      queueStats.hidden = state.previewJobStatus !== "queued";
      setText(queuePosition, job.position ? String(job.position) : "--", false);
      setText(queueLength, job.queue_length ? String(job.queue_length) : "--", false);
    }

    function setBusy(isBusy) {
      busyOverlay.hidden = !isBusy;
    }

    function currentVariantId() {
      var idInput = form.querySelector("[name='id']");
      return idInput ? String(idInput.value || "") : "";
    }

    function currentVariant() {
      if (variantsElement && Object.prototype.hasOwnProperty.call(variantsElement, "currentVariant")) {
        return variantsElement.currentVariant || null;
      }
      var variantId = currentVariantId();
      var matchingVariant = variants.find(function (variant) {
        return String(variant.id) === variantId;
      });
      if (matchingVariant) {
        return matchingVariant;
      }
      if (variantId && !variants.length) {
        return {
          id: variantId,
          available: root.getAttribute("data-availability") !== "false"
        };
      }
      return null;
    }

    function syncModalVariant() {
      if (!modalVariantSelect) {
        return;
      }

      var variant = currentVariant();
      if (variant && modalVariantSelect.value !== String(variant.id)) {
        modalVariantSelect.value = String(variant.id);
      }
      modalVariantSelect.disabled = state.addInProgress;
    }

    function selectVariantFromModal(variantId) {
      var variant = variants.find(function (candidate) {
        return String(candidate.id) === String(variantId);
      });
      if (!variant || variant.available === false) {
        syncModalVariant();
        return;
      }

      if (variantsElement) {
        var groups = variantsElement.querySelectorAll(".product-variant");
        variant.options.forEach(function (optionValue, index) {
          var group = groups[index];
          if (!group) {
            return;
          }

          if (variantsElement.dataset.type === "select") {
            var select = group.querySelector(".product-variant-container");
            if (select) {
              select.value = optionValue;
            }
            return;
          }

          group.querySelectorAll(".product-variant__input").forEach(function (input) {
            input.checked = input.value === optionValue;
          });
        });
        variantsElement.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }

      var idInput = form.querySelector("[name='id']");
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    function syncExpressCheckout() {
      if (!dynamicCheckout || !expressNote) {
        return;
      }

      var variant = currentVariant();
      var available = Boolean(variant && variant.available !== false);
      var showCheckout = available && state.previewReady && state.uploadReady &&
        !state.previewProcessing && !state.uploadProcessing;
      dynamicCheckout.hidden = !showCheckout;
      dynamicCheckout.classList.toggle("is-disabled", state.addInProgress);
      dynamicCheckout.setAttribute("aria-busy", state.addInProgress ? "true" : "false");
      dynamicCheckout.querySelectorAll("button, input, [role='button']").forEach(function (control) {
        if (state.addInProgress) {
          if ("disabled" in control && !control.disabled) {
            control.disabled = true;
            control.dataset.shimmergiftPurchaseDisabled = "true";
          }
          if (!control.hasAttribute("aria-disabled")) {
            control.dataset.shimmergiftPurchaseAriaDisabled = "true";
          }
          control.setAttribute("aria-disabled", "true");
        } else {
          if (control.dataset.shimmergiftPurchaseDisabled === "true") {
            control.disabled = false;
            delete control.dataset.shimmergiftPurchaseDisabled;
          }
          if (control.dataset.shimmergiftPurchaseAriaDisabled === "true") {
            control.removeAttribute("aria-disabled");
            delete control.dataset.shimmergiftPurchaseAriaDisabled;
          }
        }
      });
      expressNote.hidden = showCheckout && !state.addInProgress;

      if (showCheckout && !state.addInProgress) {
        expressNote.textContent = "";
        return;
      }

      if (state.addInProgress) {
        expressNote.textContent = state.purchaseAction === "buy-now"
          ? "Opening secure checkout. Please wait."
          : "Adding your custom print to the cart.";
      } else if (!available) {
        expressNote.textContent = "This option is currently unavailable.";
      } else if (state.previewProcessing) {
        expressNote.textContent = "Buy it now unlocks after your preview is ready.";
      } else if (state.uploadProcessing) {
        expressNote.textContent = "Buy it now unlocks after we save the original production photo.";
      } else if (!state.previewReady) {
        expressNote.textContent = "Buy it now unlocks after your photo finishes processing.";
      } else {
        expressNote.textContent = "Buy it now is unavailable until your production photo upload succeeds.";
      }
    }

    function beginPurchase(action) {
      if (state.addInProgress) {
        return false;
      }

      if (purchaseUnlockTimer) {
        window.clearTimeout(purchaseUnlockTimer);
        purchaseUnlockTimer = 0;
      }

      state.addInProgress = true;
      state.purchaseAction = action;
      syncExpressCheckout();
      syncAddButton();
      syncUploadControls();

      if (action === "buy-now") {
        purchaseUnlockTimer = window.setTimeout(function () {
          endPurchase();
        }, 10000);
      }
      return true;
    }

    function endPurchase() {
      if (purchaseUnlockTimer) {
        window.clearTimeout(purchaseUnlockTimer);
        purchaseUnlockTimer = 0;
      }
      state.addInProgress = false;
      state.purchaseAction = "";
      syncExpressCheckout();
      syncAddButton();
      syncUploadControls();
    }

    function syncUploadControls() {
      var disabled = state.previewProcessing || state.uploadProcessing || state.addInProgress;
      uploadTriggers.forEach(function (button) {
        button.disabled = disabled;
        button.classList.toggle("button--working", disabled);
      });
      fileInput.disabled = disabled;
    }

    function syncAddButton() {
      var variant = currentVariant();
      var available = Boolean(variant && variant.available !== false);
      var disabled = !available || !state.previewReady || !state.uploadReady ||
        state.previewProcessing || state.uploadProcessing || state.addInProgress;

      addButton.disabled = disabled;
      addButton.classList.toggle("disabled", disabled);
      syncModalVariant();

      if (!available) {
        addText.textContent = "Sold out";
        return;
      }
      if (state.addInProgress) {
        addText.textContent = state.purchaseAction === "buy-now"
          ? "Checkout in progress..."
          : "Adding to cart...";
        return;
      }
      if (state.previewProcessing) {
        addText.textContent = "Building your preview...";
        return;
      }
      if (state.uploadProcessing) {
        addText.textContent = "Saving your photo...";
        return;
      }
      if (!state.previewReady) {
        addText.textContent = "Upload a photo to continue";
        return;
      }
      if (!state.uploadReady) {
        addText.textContent = "Finish setup and add to cart";
        return;
      }
      addText.textContent = "Add custom print to cart";
    }

    function revokePreviewUrls() {
      if (state.foregroundUrl) {
        URL.revokeObjectURL(state.foregroundUrl);
      }
      if (state.backgroundUrl) {
        URL.revokeObjectURL(state.backgroundUrl);
      }
      state.foregroundUrl = "";
      state.backgroundUrl = "";
    }

    function clearProperties() {
      propertiesNode.innerHTML = "";
    }

    function injectProperties(upload) {
      clearProperties();

      var values = [
        ["properties[Uploaded photo]", upload.filename],
        ["properties[_shimmergift_upload_id]", upload.item_id],
        ["properties[_shimmergift_blob_path]", upload.blob_path],
        ["properties[_shimmergift_content_type]", upload.content_type],
        ["properties[_shimmergift_size_bytes]", upload.size_bytes],
        ["properties[_shimmergift_uploaded_at]", upload.uploaded_at],
        ["properties[_shimmergift_variant_id]", currentVariantId()],
        ["properties[_shimmergift_preview_orientation]", state.orientation],
        ["properties[_shimmergift_source]", "shopify-hologram-preview"]
      ];

      values.forEach(function (entry) {
        var input = document.createElement("input");
        input.type = "hidden";
        input.name = entry[0];
        input.value = entry[1] == null ? "" : String(entry[1]);
        propertiesNode.appendChild(input);
      });
    }

    function resetUploadState() {
      state.uploadReady = false;
      state.uploadProcessing = false;
      state.uploadMetadata = null;
      state.setupFailed = false;
      clearProperties();
      setUploadStatus("");
      syncExpressCheckout();
    }

    function resetPreviewState(keepMessages) {
      state.previewReady = false;
      state.previewFailed = false;
      state.setupFailed = false;
      state.previewJobStatus = "";
      state.orientation = "portrait";
      resetUploadState();
      revokePreviewUrls();
      viewer.reset();
      if (!keepMessages) {
        setStatus("");
        setError("");
      }
      syncAddButton();
    }

    function applyPrintLayers(result) {
      if (!result || !result.foreground || !result.background) {
        return Promise.reject(new Error("Preview finished without the required holographic layers."));
      }
      revokePreviewUrls();
      state.foregroundUrl = base64ToObjectUrl(result.foreground);
      state.backgroundUrl = base64ToObjectUrl(result.background);
      state.orientation = result.orientation || "portrait";
      return viewer.setImages(state.foregroundUrl, state.backgroundUrl, state.orientation).then(function () {
        state.previewReady = true;
      });
    }

    async function requestPreviewBlocking(file) {
      var formData = new FormData();
      formData.append("file", file, file.name || "upload.png");

      var response = await fetchWithTimeout(
        apiBase + "/create-print-layers/web",
        {
          method: "POST",
          body: formData
        },
        POLL_TIMEOUT_MS,
        "The holographic preview took too long. Please try again."
      );

      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, "Preview request failed. Please try again."));
      }

      return response.json();
    }

    async function requestPreviewQueued(file) {
      var formData = new FormData();
      formData.append("file", file, file.name || "upload.png");

      var response = await fetchWithTimeout(
        apiBase + "/create-print-layers/web/jobs",
        {
          method: "POST",
          body: formData
        },
        API_REQUEST_TIMEOUT_MS,
        "The preview upload took too long. Please try again."
      );

      if (response.status === 404 || response.status === 405) {
        setText(modalTitle, "Rendering your holographic preview", false);
        setText(modalMessage, "Processing your image.", false);
        setText(modalPhase, "The queue endpoint is unavailable, so we're rendering directly.", false);
        return requestPreviewBlocking(file);
      }

      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, "Preview upload failed. Please try again."));
      }

      var job = await response.json();
      var pollStartedAt = Date.now();
      updateQueueStats(job);
      setText(modalTitle, "Preparing your holographic preview", false);
      setText(modalMessage, buildStatusMessage(job), false);
      setText(modalPhase, buildQueuePhase(job), false);

      while (job.status === "queued" || job.status === "processing") {
        if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
          throw new Error("The preview is taking longer than expected. Please try again in a moment.");
        }
        await sleep(POLL_INTERVAL_MS);
        var statusResponse = await fetchWithTimeout(
          apiBase + "/create-print-layers/jobs/" + encodeURIComponent(job.job_id),
          {},
          STATUS_REQUEST_TIMEOUT_MS,
          "The preview status check took too long. Please try again."
        );
        if (!statusResponse.ok) {
          throw new Error(await responseErrorMessage(statusResponse, "Preview status check failed. Please try again."));
        }
        job = await statusResponse.json();
        updateQueueStats(job);
        setText(modalMessage, buildStatusMessage(job), false);
        setText(modalPhase, buildQueuePhase(job), false);
      }

      if (job.status === "failed") {
        throw new Error("Preview processing failed. Please try a different image.");
      }

      if (!job.result) {
        throw new Error("Preview finished without image data.");
      }

      return job.result;
    }

    async function fetchCartToken() {
      try {
        var response = await fetchWithTimeout(
          "/cart.js",
          {
            headers: {
              Accept: "application/json"
            }
          },
          CART_REQUEST_TIMEOUT_MS,
          "The cart request took too long."
        );
        if (!response.ok) {
          return "";
        }
        var cart = await response.json();
        return cart.token || "";
      } catch (error) {
        return "";
      }
    }

    async function uploadOriginalImage() {
      var uploadForm = new FormData();
      var cartToken = await fetchCartToken();

      uploadForm.append("file", state.file, state.file.name || "upload.png");
      if (cartToken) {
        uploadForm.append("cart_token", cartToken);
      }
      var response = await fetchWithTimeout(
        apiBase + "/cart/upload-image",
        {
          method: "POST",
          body: uploadForm
        },
        API_REQUEST_TIMEOUT_MS,
        "Saving the original photo took too long. Please retry checkout setup."
      );

      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, "Upload failed while saving your custom photo."));
      }

      return response.json();
    }

    async function ensureUploadReady(options) {
      if (state.uploadMetadata) {
        injectProperties(state.uploadMetadata);
        state.uploadReady = true;
        state.setupFailed = false;
        syncExpressCheckout();
        syncAddButton();
        return state.uploadMetadata;
      }

      state.uploadProcessing = true;
      state.uploadReady = false;
      state.setupFailed = false;
      setUploadStatus("Saving your original full-resolution photo for checkout.");
      syncExpressCheckout();
      syncAddButton();
      syncUploadControls();

      if (options && options.openModal) {
        openModal(options.title, options.message, options.phase);
      }

      try {
        var upload = await uploadOriginalImage();
        state.uploadMetadata = upload;
        state.uploadReady = true;
        state.setupFailed = false;
        injectProperties(upload);
        setUploadStatus("Original production photo saved. Buy it now is ready.");
        return upload;
      } finally {
        state.uploadProcessing = false;
        syncExpressCheckout();
        syncAddButton();
        syncUploadControls();
      }
    }

    async function retryCheckoutSetup() {
      if (
        !state.file || !state.previewReady || state.previewProcessing ||
        state.uploadProcessing || state.addInProgress
      ) {
        return;
      }

      state.setupFailed = false;
      setError("");
      setStatus("Preview ready. Retrying checkout setup.");
      setUploadStatus("Saving your original full-resolution photo for checkout.");
      setText(modalTitle, "Finishing checkout setup", false);
      setText(modalMessage, "Your holographic preview is ready.", false);
      setText(modalPhase, "Saving the original production image so it can be attached to your order.", false);
      setModalMode("saving");
      syncAddButton();
      syncUploadControls();

      try {
        await ensureUploadReady({
          openModal: false
        });
        setStatus("Preview ready. Open it anytime to complete your purchase.");
        setText(modalTitle, "Your holographic preview", false);
        setText(modalMessage, "Move across the image to see the foil shift, then add your custom print to cart or buy it now.", false);
        setText(modalPhase, "Your preview and production photo are both ready.", false);
        setModalMode("ready");
      } catch (error) {
        var message = error && error.message ? error.message : "We couldn't save the original photo for checkout.";
        state.setupFailed = true;
        setError("Your preview is ready, but checkout setup needs attention. " + message);
        setUploadStatus("The production photo was not saved. Retry checkout setup to continue.");
        setText(modalTitle, "Preview ready — checkout setup failed", false);
        setText(modalMessage, message, false);
        setText(modalPhase, "Retry checkout setup without regenerating your preview.", false);
        setModalMode("setup-error");
      } finally {
        syncExpressCheckout();
        syncAddButton();
        syncUploadControls();
      }
    }

    async function addToCart() {
      if (state.addInProgress || state.previewProcessing || state.uploadProcessing) {
        return;
      }
      if (!state.file || !state.previewReady) {
        setError("Upload a photo and wait for the preview before adding to cart.");
        return;
      }

      var variant = currentVariant();
      if (!variant || variant.available === false) {
        setError("This option is currently unavailable.");
        return;
      }

      if (!beginPurchase("add-to-cart")) {
        return;
      }
      setError("");
      setStatus("Adding your custom holographic print to cart.");
      setModalMode("ready");
      openModal(
        "Adding your custom print",
        "Preparing your cart item.",
        "Attaching your saved production file and product metadata."
      );
      setPreviewBadge("Adding to cart", "processing");
      syncExpressCheckout();
      syncAddButton();
      syncUploadControls();

      var addedSuccessfully = false;
      try {
        await ensureUploadReady({
          openModal: false
        });

        var formData = new FormData(form);
        var cartAddUrl = window.KROWN && window.KROWN.settings && window.KROWN.settings.routes
          ? window.KROWN.settings.routes.cart_add_url
          : "/cart/add.js";
        if (!/\.js$/.test(cartAddUrl)) {
          cartAddUrl += ".js";
        }
        var addResponse = await fetchWithTimeout(
          cartAddUrl,
          {
            method: "POST",
            headers: {
              Accept: "application/json"
            },
            body: formData
          },
          CART_REQUEST_TIMEOUT_MS,
          "Adding the custom print to your cart took too long. Please try again."
        );

        if (!addResponse.ok) {
          throw new Error(await responseErrorMessage(addResponse, "We couldn't add this custom print to your cart."));
        }

        addedSuccessfully = true;
        setText(modalTitle, "Added to cart", false);
        setText(modalMessage, "Your custom holographic print is in the cart.", false);
        setText(modalPhase, "Opening your cart so you can review everything.", false);
        setPreviewBadge("Added", "ready");
        window.setTimeout(function () {
          window.location.href =
            window.KROWN && window.KROWN.settings && window.KROWN.settings.routes
              ? window.KROWN.settings.routes.cart_url
              : "/cart";
        }, 450);
      } catch (error) {
        setError(error && error.message ? error.message : "We couldn't add this custom print to the cart.");
        setText(modalTitle, "Add to cart failed", false);
        setText(modalMessage, error && error.message ? error.message : "We couldn't add this custom print to the cart.", false);
        setText(modalPhase, "Please try again.", false);
        setModalMode("purchase-error");
      } finally {
        if (!addedSuccessfully) {
          endPurchase();
        }
      }
    }

    async function processFile(file) {
      if (state.previewProcessing || state.uploadProcessing || state.addInProgress) {
        setStatus("Please wait for the current preview to finish.");
        return;
      }
      if (!file || ALLOWED_IMAGE_TYPES.indexOf(String(file.type || "").toLowerCase()) === -1) {
        setError("Please choose a JPEG, PNG, WebP, GIF, or TIFF image.");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setError("Please choose an image smaller than 20 MB.");
        return;
      }

      state.file = file;
      state.previewProcessing = true;
      resetPreviewState(true);
      openPreviewButton.hidden = false;
      setFileName(file.name || "Custom image");
      setStatus("Uploading image.");
      setUploadStatus("");
      setError("");
      setBusy(true);
      setModalMode("processing");
      syncExpressCheckout();
      syncAddButton();
      syncUploadControls();
      openModal(
        "Preparing your holographic preview",
        "Uploading image.",
        "Checking the queue and reserving a render slot."
      );
      updateQueueStats({ status: "", position: null, queue_length: null });

      var previewCompleted = false;
      try {
        var result = await requestPreviewQueued(file);
        await applyPrintLayers(result);
        previewCompleted = true;
        state.previewProcessing = false;
        setBusy(false);
        setStatus("Preview ready. Saving the original photo for checkout.");
        setText(modalTitle, "Your holographic preview", false);
        setText(modalMessage, "Your holographic preview is ready.", false);
        setText(modalPhase, "Saving the original production image so checkout can use it.", false);
        setModalMode("saving");

        await ensureUploadReady({
          openModal: false
        });

        setStatus("Preview ready. Open it anytime to complete your purchase.");
        setText(modalTitle, "Your holographic preview", false);
        setText(modalMessage, "Move across the image to see the foil shift, then add your custom print to cart or buy it now.", false);
        setText(modalPhase, "Your preview and production photo are both ready.", false);
        setModalMode("ready");
      } catch (error) {
        var message = error && error.message ? error.message : "We couldn't generate that preview.";

        if (previewCompleted) {
          state.previewFailed = false;
          state.setupFailed = true;
          setError("Your preview is ready, but checkout setup needs attention. " + message);
          setUploadStatus("The production photo was not saved. Retry checkout setup to continue.");
          setText(modalTitle, "Preview ready — checkout setup failed", false);
          setText(modalMessage, message, false);
          setText(modalPhase, "Retry checkout setup without regenerating your preview.", false);
          setModalMode("setup-error");
        } else {
          state.previewFailed = true;
          state.setupFailed = false;
          setError(message);
          setText(modalTitle, "Preview failed", false);
          setText(modalMessage, message, false);
          setText(modalPhase, "Try a different image or upload again.", false);
          setModalMode("error");
        }
      } finally {
        state.previewProcessing = false;
        setBusy(false);
        syncExpressCheckout();
        syncAddButton();
        syncUploadControls();
      }
    }

    closeButtons.forEach(function (button) {
      button.addEventListener("click", closeModal);
    });

    uploadTriggers.forEach(function (button) {
      button.addEventListener("click", function (event) {
        event.preventDefault();
        if (!fileInput.disabled) {
          if (!modal.hidden && modal.contains(button)) {
            closeModal();
          }
          fileInput.click();
        }
      });
    });

    openPreviewButton.addEventListener("click", function (event) {
      event.preventDefault();
      if (state.previewProcessing || state.uploadProcessing) {
        setModalMode(state.previewReady ? "saving" : "processing");
      } else if (state.setupFailed) {
        setModalMode("setup-error");
      } else if (state.previewReady && state.uploadReady) {
        setModalMode("ready");
      } else if (state.previewFailed) {
        setModalMode("error");
      } else {
        setModalMode("processing");
      }
      openModal();
    });

    retryUploadButton.addEventListener("click", function (event) {
      event.preventDefault();
      retryCheckoutSetup();
    });

    if (modalVariantSelect) {
      modalVariantSelect.addEventListener("change", function () {
        selectVariantFromModal(modalVariantSelect.value);
      });
    }

    if (dynamicCheckout) {
      dynamicCheckout.addEventListener("click", function (event) {
        if (state.addInProgress || buyNowLockPending) {
          event.preventDefault();
          return;
        }
        buyNowLockPending = true;
        window.setTimeout(function () {
          buyNowLockPending = false;
          beginPurchase("buy-now");
        }, 0);
      });
    }

    fileInput.addEventListener("change", function (event) {
      var file = event.target.files && event.target.files[0];
      if (file) {
        processFile(file);
      }
      fileInput.value = "";
    });

    dropzone.addEventListener("dragover", function (event) {
      event.preventDefault();
      if (fileInput.disabled) {
        return;
      }
      dropzone.classList.add("is-dragging");
    });

    dropzone.addEventListener("dragleave", function () {
      dropzone.classList.remove("is-dragging");
    });

    dropzone.addEventListener("drop", function (event) {
      event.preventDefault();
      dropzone.classList.remove("is-dragging");
      if (fileInput.disabled) {
        return;
      }
      var file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) {
        processFile(file);
      }
    });

    root.addEventListener("paste", function (event) {
      var items = event.clipboardData && event.clipboardData.items;
      if (!items) {
        return;
      }
      for (var index = 0; index < items.length; index += 1) {
        var item = items[index];
        if (item.type && item.type.indexOf("image/") === 0) {
          var file = item.getAsFile();
          if (file) {
            processFile(file);
            event.preventDefault();
            event.stopPropagation();
            return;
          }
        }
      }
    });

    function handleDocumentPaste(event) {
      if (event.defaultPrevented || root.contains(event.target)) {
        return;
      }
      if (!root.isConnected || document.activeElement && /input|textarea/i.test(document.activeElement.tagName)) {
        return;
      }
      if (modal.hidden && document.activeElement !== dropzone && !root.contains(document.activeElement)) {
        return;
      }
      var items = event.clipboardData && event.clipboardData.items;
      if (!items) {
        return;
      }
      for (var index = 0; index < items.length; index += 1) {
        var item = items[index];
        if (item.type && item.type.indexOf("image/") === 0) {
          var file = item.getAsFile();
          if (file) {
            processFile(file);
            event.preventDefault();
            return;
          }
        }
      }
    }

    document.addEventListener("paste", handleDocumentPaste);

    function handleSectionUnload(event) {
      if (!event.target || (event.target !== root && !event.target.contains(root))) {
        return;
      }

      if (!modal.hidden) {
        closeModal(false);
      } else {
        viewer.pause();
        document.documentElement.classList.remove("shimmergift-modal-open");
        restorePageAfterModal();
      }
      revokePreviewUrls();
      document.removeEventListener("paste", handleDocumentPaste);
      document.removeEventListener("shopify:section:unload", handleSectionUnload);
    }

    document.addEventListener("shopify:section:unload", handleSectionUnload);

    addButton.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      addToCart();
    });

    modal.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }

      var focusable = Array.prototype.filter.call(
        modalDialog.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"),
        function (element) {
          return !element.hidden && element.getClientRects().length > 0;
        }
      );
      if (!focusable.length) {
        event.preventDefault();
        modalDialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === modalDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    form.addEventListener("submit", function (event) {
      if (
        !state.previewReady || !state.uploadReady || state.previewProcessing ||
        state.uploadProcessing || (state.addInProgress && state.purchaseAction !== "buy-now")
      ) {
        event.preventDefault();
        event.stopPropagation();
        setError("Upload a photo and wait for processing to finish before checkout.");
        return;
      }
      if (!state.addInProgress) {
        beginPurchase("buy-now");
      }
    });

    root.addEventListener("change", function (event) {
      if (event.target && event.target.name === "id") {
        if (state.uploadMetadata) {
          injectProperties(state.uploadMetadata);
        }
        syncExpressCheckout();
        syncAddButton();
      }
    });

    if (variantsElement) {
      variantsElement.addEventListener("VARIANT_CHANGE", function () {
        if (state.uploadMetadata) {
          injectProperties(state.uploadMetadata);
        }
        syncExpressCheckout();
        syncAddButton();
      });
    }

    syncExpressCheckout();
    syncAddButton();
    syncUploadControls();
  }

  function initAll() {
    var roots = document.querySelectorAll("[data-shimmergift-root]");
    for (var index = 0; index < roots.length; index += 1) {
      initShimmergiftProduct(roots[index]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
})();
